const STORAGE_KEY = 'docNavigationContext';

/**
 * Persists the filtered document ID list for prev/next navigation on DocumentDetails.
 * Updated whenever the grid's filtered rows change (queue switch, vendor toggle, column filters).
 * Uses sessionStorage so context is scoped to the browser tab.
 */
export function saveDocumentNavigationContext(viewKey, ids) {
  if (!viewKey) {
    return;
  }

  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        viewKey,
        ids: Array.isArray(ids) ? ids : [],
      }),
    );
  } catch (error) {
    console.error('Failed to save document navigation context:', error);
  }
}

export function getDocumentNavigationContext() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed?.viewKey || !Array.isArray(parsed.ids)) {
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Failed to read document navigation context:', error);
    return null;
  }
}

/**
 * Returns adjacent document IDs from the cached filtered grid list.
 * Returns null when the current document is not in the list (e.g. opened via search or direct URL).
 */
export function getAdjacentDocumentIds(currentDocumentId) {
  const context = getDocumentNavigationContext();
  if (!context || !currentDocumentId) {
    return null;
  }

  const index = context.ids.indexOf(currentDocumentId);
  if (index === -1) {
    return null;
  }

  return {
    prev: index > 0 ? context.ids[index - 1] : null,
    next: index < context.ids.length - 1 ? context.ids[index + 1] : null,
  };
}

/**
 * Removes a document from the cached navigation list after it leaves the current working set
 * (e.g. squash, update, approve). Does not auto-advance to the next document.
 */
export function removeDocumentFromNavigationContext(documentId) {
  const context = getDocumentNavigationContext();
  if (!context || !documentId) {
    return;
  }

  saveDocumentNavigationContext(
    context.viewKey,
    context.ids.filter((id) => id !== documentId),
  );
}
